import React, { Fragment, useState, useEffect, useCallback } from "react";
import { Formik, Form, FieldArray } from "formik";
import { useQuery, useMutation } from "urql";
import { cloneDeep, sortBy } from "lodash";
import { useParams, useLocation } from "wouter";
import { Button } from "../../../components/src/Button";
import { Alert, AlertVariant } from "../../../components/src/Alert";

import { AttachmentForm } from "./AttachmentForm";
import { usePrevious, useAlertTimeOut } from "../../../components/src/common/Utils";
import { SubmitButton, TextField, SelectField, SearchMultipleSelectField } from "../../../components/src/form";
import { GET_AGREEMENTS, GET_AGREEMENT_PARTIES, UPSERT_AGREEMENTS } from "../../../graphql/src/agreement/queries";
import {
    Agreement,
    AgreementTypeEnum,
    AgreementStatusEnum,
    AgreementFilterInput,
    AgreementInput,
    ApprovedStatusEnum,
    ApprovedInfo,
    Party
} from "../types.generated";
import { SYSTEM_PARTY_ID } from "../Constants";
import { MarkDownField } from "../components/form/MarkDownField";
import { recursivelyRemoveKey } from "../../../common/src/utils";
import { REACT_APP_API_URI } from "../env";

const getDefaultAgreement = (): AgreementInput => {
    const agreement: AgreementInput = {
        _id: "new",
        attachments: [],
        clientId: SYSTEM_PARTY_ID,
        counterPartyIds: [],
        description: "",
        partyIds: [],
        status: AgreementStatusEnum.Active,
        type: AgreementTypeEnum.Gmsla,
        approvedInfo: []
    };

    return agreement;
};

export function AgreementPage(): React.ReactElement {
    const { id } = useParams<{ id: string }>();
    const [, navigate] = useLocation();
    const previousId: string = usePrevious(id);
    const isEditMode = id === "new" ? false : true;
    const isCreateMode = !isEditMode;

    const filter: AgreementFilterInput = { idIn: [id], statusIn: Object.values(AgreementStatusEnum) };
    const [{ fetching: loading, error, data }, refetch] = useQuery<{ agreements?: Partial<Agreement>[] }>({
        query: GET_AGREEMENTS,
        variables: { filter: filter },
        pause: isCreateMode,
        requestPolicy: "cache-and-network"
    });
    const [_, upsertMutation] = useMutation(UPSERT_AGREEMENTS);

    const [{ fetching: loadingParties, error: errorParties, data: dataParties }] = useQuery<{
        clients?: Partial<Party>[];
        parties?: Partial<Party>[];
    }>({ query: GET_AGREEMENT_PARTIES });
    const [agreement, setAgreement] = useState(null);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    useEffect(() => {
        if (id === "new" && !agreement) {
            setAgreement(getDefaultAgreement());
        } else if (previousId && id !== previousId) {
            setAgreement(null);
        } else if (data) {
            setAgreement(data.agreements[0]);
        }
    }, [agreement, data, id, previousId]);

    const callBackOnChangeAttachment = useCallback(
        (attachments) => {
            const approvedInfo: ApprovedInfo[] = [];
            attachments = recursivelyRemoveKey(attachments, "__typename");
            if (agreement.approvedInfo && agreement.approvedInfo.length) {
                for (const info of cloneDeep(agreement.approvedInfo)) {
                    if (info.updateUserInfo) {
                        delete info.updateUserInfo;
                    }
                    delete info.__typename;
                    approvedInfo.push(info);
                }
            }
            const input: AgreementInput = {
                _id: agreement._id,
                clientId: agreement.clientId,
                counterPartyIds: agreement.counterPartyIds,
                description: agreement.description,
                partyIds: agreement.partyIds,
                status: agreement.status,
                type: agreement.type,
                approvedInfo: approvedInfo,
                attachments: attachments
            };
            upsertMutation({ input: input }).then(() => {
                if (isEditMode) {
                    refetch();
                }
            });
        },
        [agreement, isEditMode, refetch, upsertMutation]
    );

    if (loading || loadingParties) return <div>Loading</div>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (errorParties) return <p>error: {JSON.stringify(errorParties, null, 2)}</p>;
    if (!agreement) return <div>No agreement found</div>;

    const partyOptions = sortBy(
        dataParties.parties.map((d) => ({ key: d._id, value: d.name, text: d.name })),
        "value"
    );

    const clientOptions = sortBy(
        dataParties.clients.map((d) => ({ key: d._id, value: d.name })),
        "value"
    );

    const defaultApprovedInfo = {
        status: ApprovedStatusEnum.Accepted,
        comment: ""
    };

    return (
        <div className="form">
            <Formik
                enableReinitialize={true}
                initialValues={agreement}
                onSubmit={async (submitValues, { setSubmitting }) => {
                    const approvedInfo: ApprovedInfo[] = [];
                    if (submitValues.approvedInfo && submitValues.approvedInfo.length) {
                        for (const info of cloneDeep(submitValues.approvedInfo)) {
                            if (info.updateUserInfo) {
                                delete info.updateUserInfo;
                            }
                            delete info.__typename;
                            approvedInfo.push(info);
                        }
                    }
                    const input: AgreementInput = {
                        _id: submitValues._id,
                        clientId: submitValues.clientId,
                        counterPartyIds: submitValues.counterPartyIds,
                        description: submitValues.description,
                        partyIds: submitValues.partyIds,
                        status: submitValues.status,
                        type: submitValues.type,
                        approvedInfo: approvedInfo
                    };
                    if (isCreateMode) {
                        delete input._id;
                    }
                    await upsertMutation({ input: [input] })
                        .then((result) => {
                            const agreement = result.data.upsertAgreements[0];
                            if (isCreateMode) {
                                navigate("/agreements/" + agreement._id, { replace: true });
                            } else {
                                setAgreement(agreement);
                                setAlert({
                                    color: "success",
                                    visible: true,
                                    message: `Agreement '${agreement._id}' updated successfully!`
                                });
                            }
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, values, errors }) => {
                    if (Object.keys(errors).length > 0) {
                        console.log(errors);
                    }
                    return (
                        <Fragment>
                            <h2 className="p-2">Agreement</h2>
                            <Form autoComplete="off">
                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full sm:w-1/2 px-2 mb-4">
                                        <TextField name="_id" label="Id" type="text" className="p-2" disabled={true} />
                                    </div>
                                    <div className="w-full sm:w-1/2 px-2 mb-4">
                                        <TextField
                                            name="updateUserInfo.name"
                                            label="Updated by"
                                            type="text"
                                            className="p-2"
                                            disabled={true}
                                        />
                                    </div>
                                </div>

                                <div className="p-2 mt-2">
                                    <MarkDownField
                                        graphqlApiBaseUrl={REACT_APP_API_URI}
                                        name="description"
                                        label="Description"
                                        className="btn btn-primary p-1 cursor-pointer"
                                        disabled={true}
                                    />
                                </div>

                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full sm:w-1/3 px-2 mb-4">
                                        <SelectField
                                            name="clientId"
                                            label="Client (owner)"
                                            options={clientOptions}
                                            className="p-2"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div className="w-full sm:w-1/3 px-2 mb-4">
                                        <SelectField
                                            name="status"
                                            label="Status"
                                            options={Object.keys(AgreementStatusEnum)}
                                            className="p-2"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div className="w-full sm:w-1/3 px-2 mb-4">
                                        <SelectField
                                            name="type"
                                            label="Type"
                                            options={Object.keys(AgreementTypeEnum)}
                                            className="p-2"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>

                                <div className="w-full px-2 mb-4">
                                    <SearchMultipleSelectField
                                        name="partyIds"
                                        label="Parties"
                                        className="w-full p-2"
                                        disabled={isSubmitting}
                                        options={partyOptions}
                                    />
                                </div>

                                <div className="w-full px-2 mb-4">
                                    <SearchMultipleSelectField
                                        name="counterPartyIds"
                                        label="Counterparties"
                                        className="w-full p-2"
                                        disabled={isSubmitting}
                                        options={partyOptions}
                                    />
                                </div>

                                <div className="p-2">
                                    <label style={{ fontWeight: "bold" }}>Approved info</label>
                                    <FieldArray
                                        name="approvedInfo"
                                        render={(arrayHelpers) => (
                                            <Fragment>
                                                {values.approvedInfo && values.approvedInfo.length > 0 ? (
                                                    values.approvedInfo.map((item, index) => (
                                                        <div key={index} className="flex flex-wrap -mx-2 mb-4">
                                                            <div className="w-full sm:w-1/4 px-2">
                                                                <SelectField
                                                                    className=""
                                                                    name={`approvedInfo[${index}].status`}
                                                                    label="Status*"
                                                                    options={Object.keys(ApprovedStatusEnum)}
                                                                    disabled={item.updateUserId ? true : false}
                                                                />
                                                                <TextField
                                                                    name={`approvedInfo[${index}].comment`}
                                                                    label="Comment"
                                                                    className=""
                                                                    disabled={item.updateUserId ? true : false}
                                                                />
                                                                <TextField
                                                                    name={`approvedInfo[${index}].updateUserInfo.name`}
                                                                    label="User"
                                                                    className=""
                                                                    disabled={true}
                                                                />
                                                                <TextField
                                                                    name={`approvedInfo[${index}].updateTimestamp`}
                                                                    label="Time"
                                                                    className=""
                                                                    disabled={true}
                                                                />
                                                            </div>
                                                            {index === 0 ? (
                                                                <div className="w-full sm:w-1/6 px-2s">
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        className="mt-4"
                                                                        type="button"
                                                                        onClick={() => arrayHelpers.insert(index, defaultApprovedInfo)}
                                                                    >
                                                                        +
                                                                    </Button>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        className="ml-1"
                                                        type="button"
                                                        onClick={() => arrayHelpers.push(defaultApprovedInfo)}
                                                    >
                                                        +
                                                    </Button>
                                                )}
                                            </Fragment>
                                        )}
                                    />
                                </div>

                                <div className="p-2">
                                    <SubmitButton disabled={isSubmitting || Object.keys(errors).length > 0} label={"Save"} />
                                </div>

                                {alert.visible ? (
                                    <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                                        {alert.message}
                                    </Alert>
                                ) : null}
                            </Form>
                        </Fragment>
                    );
                }}
            </Formik>

            <div className="pb-3">
                {isEditMode ? (
                    <AttachmentForm
                        clientId={agreement.clientId}
                        attachments={agreement.attachments}
                        onChange={callBackOnChangeAttachment}
                    />
                ) : null}
            </div>
        </div>
    );
}
